/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import {
  CartSummaryGrid as CartSummaryGridComponent,
  EmptyCart,
} from '@/cart/components';
import { CartModel } from '@/cart/data/models';
import { VNode } from 'preact';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import { Image, ImageProps } from '@adobe-commerce/elsie/components';
import { getPersistedCartData } from '@/cart/lib/persisted-data';

export interface CartSummaryGridProps extends HTMLAttributes<HTMLDivElement> {
  routeProduct?: (item: CartModel['items'][0]) => string;
  routeEmptyCartCTA?: () => string;
  slots?: {
    Thumbnail?: SlotProps<{ 
      item: CartModel['items'][number], 
      defaultImageProps: ImageProps 
    }>;
  };
}

export const CartSummaryGrid: Container<
  CartSummaryGridProps,
  CartModel | null
> = ({
  children,
  initialData = null,
  routeProduct,
  routeEmptyCartCTA,
  slots,
  ...props
}) => {
  const [data, setData] = useState<CartModel | null>(initialData);

  useEffect(() => {
    const dataEvent = events.on(
      'cart/data',
      (payload) => {
        setData(payload as CartModel);
      },
      { eager: true }
    );
    return () => {
      dataEvent?.off();
    };
  }, []);

  const getImage = (item: CartModel['items'][0], index: number) => {
    const handleAriaLabel = item.selectedOptions
      ? `${item.name}: ${Object.entries(item.selectedOptions).join('; ')}`
      : item.name;

    const defaultImageProps: ImageProps = {
      loading: index < 4 ? 'eager' : 'lazy',
      src: item.image.src,
      alt: item.image.alt,
    };

    const imageComponent = (
      <Image
        data-testid="cart-grid-item-image"
        aria-label={handleAriaLabel}
        width={'100%'}
        {...defaultImageProps}
      />
    );

    return (
      <div className="cart-cart-summary-grid__item-container" key={index}>
        <Slot name="Thumbnail" slot={slots?.Thumbnail} context={{ item, defaultImageProps }}>
          {routeProduct ? (
              <a href={routeProduct(item)}>{imageComponent}</a>
            ) : (
              imageComponent
            )}
        </Slot>
      </div>
    );
  };

  const products =
    data &&
    ((
      <>
        {data.items.map((item, index) => {
          return getImage(item, index) as VNode;
        })}
      </>
    ) as VNode | null);

  return (
    <CartSummaryGridComponent
      {...props}
      emptyCart={
        <EmptyCart
          ctaLinkURL={routeEmptyCartCTA ? routeEmptyCartCTA() : undefined}
        />
      }
      products={products}
    />
  );
};

CartSummaryGrid.getInitialData = async function () {
  return getPersistedCartData();
};
